import { Component } from '@theme/component';

/**
 * UGC Stories custom element that provides Instagram Stories-like functionality
 * for displaying user-generated content (images and videos).
 *
 * @typedef {Object} Refs
 * @property {HTMLDialogElement} storiesDialog
 * @property {HTMLButtonElement} triggerButton
 * @property {HTMLElement} progressBar
 * @property {HTMLElement[]} progressFill
 * @property {HTMLElement} mediaContainer
 * @property {HTMLElement[]} slides
 * @property {HTMLVideoElement[]} [videos]
 * @property {HTMLVideoElement[]} [carouselVideos]
 * @property {HTMLElement} [carousel]
 * @property {HTMLButtonElement} prevButton
 * @property {HTMLButtonElement} nextButton
 * @property {HTMLButtonElement} pauseButton
 * @property {HTMLButtonElement} muteButton
 * @property {HTMLElement} iconPause
 * @property {HTMLElement} iconPlay
 * @property {HTMLElement} iconSoundOn
 * @property {HTMLElement} iconSoundOff
 * @property {HTMLElement} [caption]
 *
 * @extends {Component<Refs>}
 */
class UGCStories extends Component {
  requiredRefs = ['storiesDialog', 'slides'];

  /** @type {number} */
  #currentIndex = 0;

  /** @type {number|undefined} */
  #progressInterval;

  /** @type {number} */
  #progressStartTime = 0;

  /** @type {number} */
  #progressElapsed = 0;

  /** @type {boolean} */
  #isPaused = false;

  /** @type {boolean} */
  #isMuted = false;

  /** @type {number} */
  #imageDuration = 5000;

  /** @type {boolean} */
  #isHolding = false;

  /** @type {IntersectionObserver|null} */
  #videoObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Get image duration from block settings (data attribute)
    const durationAttr = this.getAttribute('data-image-duration');
    if (durationAttr) {
      this.#imageDuration = parseInt(durationAttr, 10) * 1000;
    }

    // Setup touch/mouse hold for pause
    this.#setupHoldListeners();

    // Close on escape key
    this.refs.storiesDialog?.addEventListener('keydown', this.#handleKeydown);

    // Setup lazy loading for carousel videos
    this.#setupLazyLoadVideos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopProgress();
    this.refs.storiesDialog?.removeEventListener('keydown', this.#handleKeydown);

    // Cleanup video observer
    if (this.#videoObserver) {
      this.#videoObserver.disconnect();
      this.#videoObserver = null;
    }
  }

  /**
   * Opens the stories dialog and starts playback
   * @param {Event} event
   */
  openStories(event) {
    this.openStoriesAt(0, event);
  }

  /**
   * Opens the stories dialog at a specific index
   * @param {number} index - The index to start at
   * @param {Event} [event]
   */
  openStoriesAt(index, event) {
    event?.preventDefault();
    const { storiesDialog } = this.refs;
    if (!storiesDialog) return;

    // Pause all carousel videos
    this.#pauseCarouselVideos();

    // Reset mute state - sound ON by default when opening
    this.#isMuted = false;
    this.#updateMuteIcon();

    this.#currentIndex = typeof index === 'number' ? index : 0;
    this.#updateSlide();
    storiesDialog.showModal();
    document.body.style.overflow = 'hidden';
    this.#startProgress();
  }

  /**
   * Closes the stories dialog
   * @param {Event} [event]
   */
  closeStories(event) {
    event?.preventDefault();
    event?.stopPropagation();
    const { storiesDialog } = this.refs;
    if (!storiesDialog) return;

    this.#stopProgress();
    this.#pauseAllVideos();
    storiesDialog.close();
    document.body.style.overflow = '';

    // Resume carousel videos
    this.#playCarouselVideos();
  }

  /**
   * Navigates to the previous slide
   * @param {Event} [event]
   */
  previousSlide(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.#currentIndex > 0) {
      this.#currentIndex--;
      this.#updateSlide();
      this.#restartProgress();
    }
  }

  /**
   * Navigates to the next slide
   * @param {Event} [event]
   */
  nextSlide(event) {
    event?.preventDefault();
    event?.stopPropagation();

    const { slides } = this.refs;
    if (!slides) return;

    if (this.#currentIndex < slides.length - 1) {
      this.#currentIndex++;
      this.#updateSlide();
      this.#restartProgress();
    } else {
      // End of stories, close the dialog
      this.closeStories();
    }
  }

  /**
   * Toggles pause/play state
   * @param {Event} [event]
   */
  togglePause(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.#isPaused) {
      this.#resumeProgress();
    } else {
      this.#pauseProgress();
    }
  }

  /**
   * Toggles mute/unmute state for videos
   * @param {Event} [event]
   */
  toggleMute(event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.#isMuted = !this.#isMuted;
    this.#updateMuteIcon();
    this.#applyMuteState();
  }

  /**
   * Handles clicks on the dialog backdrop to close
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    const { storiesDialog } = this.refs;
    if (event.target === storiesDialog) {
      this.closeStories(event);
    }
  }

  /**
   * Updates the visible slide and progress indicators
   */
  #updateSlide() {
    const { slides, progressBar } = this.refs;
    if (!slides) return;

    // Update slide visibility
    slides.forEach((slide, index) => {
      slide.classList.toggle('ugc-stories-dialog__slide--active', index === this.#currentIndex);
    });

    // Update progress segments
    if (progressBar) {
      const segments = progressBar.querySelectorAll('.ugc-stories-dialog__progress-segment');
      segments.forEach((segment, index) => {
        const fill = segment.querySelector('.ugc-stories-dialog__progress-fill');
        if (fill instanceof HTMLElement) {
          if (index < this.#currentIndex) {
            segment.setAttribute('data-completed', 'true');
            fill.style.width = '100%';
          } else if (index > this.#currentIndex) {
            segment.removeAttribute('data-completed');
            fill.style.width = '0%';
          } else {
            segment.removeAttribute('data-completed');
            fill.style.width = '0%';
          }
        }
      });
    }

    // Handle video playback
    this.#pauseAllVideos();
    const currentSlide = slides[this.#currentIndex];
    if (currentSlide?.dataset.mediaType === 'video') {
      const video = currentSlide.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.muted = this.#isMuted;
        video.play().catch(() => {});
      }
    }
  }

  /**
   * Starts the progress animation for the current slide
   */
  #startProgress() {
    this.#progressStartTime = performance.now();
    this.#progressElapsed = 0;
    this.#isPaused = false;
    this.#updatePauseIcon();

    const currentSlide = this.refs.slides?.[this.#currentIndex];
    const isVideo = currentSlide?.dataset.mediaType === 'video';

    if (isVideo) {
      // For videos, progress is tied to video duration
      const video = currentSlide?.querySelector('video');
      if (video) {
        video.addEventListener('ended', this.#handleVideoEnded);
        video.addEventListener('timeupdate', this.#handleVideoTimeUpdate);
      }
    } else {
      // For images, use the configured duration
      this.#runProgressTimer();
    }
  }

  /**
   * Runs the progress timer for images
   */
  #runProgressTimer() {
    const tick = () => {
      if (this.#isPaused || this.#isHolding) {
        this.#progressInterval = requestAnimationFrame(tick);
        return;
      }

      const elapsed = performance.now() - this.#progressStartTime + this.#progressElapsed;
      const progress = Math.min(elapsed / this.#imageDuration, 1);

      this.#updateProgressBar(progress);

      if (progress >= 1) {
        this.nextSlide();
      } else {
        this.#progressInterval = requestAnimationFrame(tick);
      }
    };

    this.#progressInterval = requestAnimationFrame(tick);
  }

  /**
   * Handles video ended event
   */
  #handleVideoEnded = () => {
    this.nextSlide();
  };

  /**
   * Handles video timeupdate event to sync progress bar
   */
  #handleVideoTimeUpdate = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;

    const progress = video.duration ? video.currentTime / video.duration : 0;
    this.#updateProgressBar(progress);
  };

  /**
   * Updates the progress bar fill for the current slide
   * @param {number} progress - Progress value between 0 and 1
   */
  #updateProgressBar(progress) {
    const { progressBar } = this.refs;
    if (!progressBar) return;

    const currentSegment = progressBar.querySelector(
      `.ugc-stories-dialog__progress-segment[data-index="${this.#currentIndex}"]`
    );
    const fill = currentSegment?.querySelector('.ugc-stories-dialog__progress-fill');

    if (fill instanceof HTMLElement) {
      fill.style.width = `${progress * 100}%`;
    }
  }

  /**
   * Pauses the progress animation
   */
  #pauseProgress() {
    this.#isPaused = true;
    this.#progressElapsed += performance.now() - this.#progressStartTime;
    this.#updatePauseIcon();

    // Pause video if playing
    const currentSlide = this.refs.slides?.[this.#currentIndex];
    if (currentSlide?.dataset.mediaType === 'video') {
      const video = currentSlide.querySelector('video');
      video?.pause();
    }
  }

  /**
   * Resumes the progress animation
   */
  #resumeProgress() {
    this.#isPaused = false;
    this.#progressStartTime = performance.now();
    this.#updatePauseIcon();

    // Resume video if applicable
    const currentSlide = this.refs.slides?.[this.#currentIndex];
    if (currentSlide?.dataset.mediaType === 'video') {
      const video = currentSlide.querySelector('video');
      video?.play().catch(() => {});
    }
  }

  /**
   * Stops and cleans up progress tracking
   */
  #stopProgress() {
    if (this.#progressInterval) {
      cancelAnimationFrame(this.#progressInterval);
      this.#progressInterval = undefined;
    }

    // Remove video event listeners
    const currentSlide = this.refs.slides?.[this.#currentIndex];
    if (currentSlide?.dataset.mediaType === 'video') {
      const video = currentSlide.querySelector('video');
      if (video) {
        video.removeEventListener('ended', this.#handleVideoEnded);
        video.removeEventListener('timeupdate', this.#handleVideoTimeUpdate);
      }
    }
  }

  /**
   * Restarts progress for a new slide
   */
  #restartProgress() {
    this.#stopProgress();
    this.#startProgress();
  }

  /**
   * Updates the pause/play icon state
   */
  #updatePauseIcon() {
    const { iconPause, iconPlay } = this.refs;
    if (iconPause) {
      iconPause.classList.toggle('hidden', this.#isPaused);
    }
    if (iconPlay) {
      iconPlay.classList.toggle('hidden', !this.#isPaused);
    }
  }

  /**
   * Updates the mute/unmute icon state
   */
  #updateMuteIcon() {
    const { iconSoundOn, iconSoundOff } = this.refs;
    if (iconSoundOn) {
      iconSoundOn.classList.toggle('hidden', this.#isMuted);
    }
    if (iconSoundOff) {
      iconSoundOff.classList.toggle('hidden', !this.#isMuted);
    }
  }

  /**
   * Applies the current mute state to all dialog videos
   */
  #applyMuteState() {
    const { videos } = this.refs;
    if (!videos) return;

    videos.forEach((video) => {
      video.muted = this.#isMuted;
    });
  }

  /**
   * Pauses all dialog videos
   */
  #pauseAllVideos() {
    const { videos } = this.refs;
    if (!videos) return;

    videos.forEach((video) => {
      video.pause();
      video.removeEventListener('ended', this.#handleVideoEnded);
      video.removeEventListener('timeupdate', this.#handleVideoTimeUpdate);
    });
  }

  /**
   * Pauses all carousel preview videos
   */
  #pauseCarouselVideos() {
    const { carouselVideos } = this.refs;
    if (!carouselVideos) return;

    carouselVideos.forEach((video) => {
      video.pause();
    });
  }

  /**
   * Plays all carousel preview videos that have been loaded
   */
  #playCarouselVideos() {
    const { carouselVideos } = this.refs;
    if (!carouselVideos) return;

    carouselVideos.forEach((video) => {
      // Only play if video has a src loaded
      if (video.src) {
        video.play().catch(() => {});
      }
    });
  }

  /**
   * Sets up IntersectionObserver for lazy loading carousel videos
   */
  #setupLazyLoadVideos() {
    const { carouselVideos } = this.refs;
    if (!carouselVideos || carouselVideos.length === 0) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;

          if (entry.isIntersecting) {
            // Load and play video when it enters viewport
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
          } else {
            // Pause video when it leaves viewport
            video.pause();
          }
        });
      },
      {
        root: this.refs.carousel,
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    carouselVideos.forEach((video) => {
      this.#videoObserver.observe(video);
    });
  }

  /**
   * Sets up touch/mouse hold listeners for pause functionality
   */
  #setupHoldListeners() {
    const { mediaContainer } = this.refs;
    if (!mediaContainer) return;

    let holdTimeout;

    const startHold = () => {
      holdTimeout = setTimeout(() => {
        this.#isHolding = true;
        this.#pauseProgress();
      }, 200);
    };

    const endHold = () => {
      clearTimeout(holdTimeout);
      if (this.#isHolding) {
        this.#isHolding = false;
        this.#resumeProgress();
      }
    };

    mediaContainer.addEventListener('pointerdown', startHold);
    mediaContainer.addEventListener('pointerup', endHold);
    mediaContainer.addEventListener('pointerleave', endHold);
    mediaContainer.addEventListener('pointercancel', endHold);
  }

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    switch (event.key) {
      case 'Escape':
        this.closeStories(event);
        break;
      case 'ArrowLeft':
        this.previousSlide(event);
        break;
      case 'ArrowRight':
        this.nextSlide(event);
        break;
      case ' ':
        event.preventDefault();
        this.togglePause(event);
        break;
      case 'm':
      case 'M':
        this.toggleMute(event);
        break;
    }
  };
}

if (!customElements.get('ugc-stories-block')) {
  customElements.define('ugc-stories-block', UGCStories);
}
